import React, { useMemo } from "react";
import { buildFacility360Metrics } from "../utils/facility360Metrics";

function InsightCard({
  title,
  description,
  tone = "neutral",
}) {
  const tones = {
    positive: {
      color: "#166534",
      background: "#f0fdf4",
      border: "#bbf7d0",
    },
    warning: {
      color: "#92400e",
      background: "#fffbeb",
      border: "#fde68a",
    },
    risk: {
      color: "#991b1b",
      background: "#fef2f2",
      border: "#fecaca",
    },
    neutral: {
      color: "#1e40af",
      background: "#eff6ff",
      border: "#bfdbfe",
    },
  };

  const colors =
    tones[tone] || tones.neutral;

  return (
    <div
      style={{
        padding: 14,
        borderRadius: 14,
        color: colors.color,
        background: colors.background,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 950,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.5,
        }}
      >
        {description}
      </div>
    </div>
  );
}

export default function Facility360AIInsights({
  facility,
  shifts = [],
  users = [],
  showMargin = true,
}) {
  const analysis = useMemo(() => {
    const sharedMetrics =
      buildFacility360Metrics({
        facility,
        shifts,
        users,
      });

    const {
      facilityWorkers,
      openShifts,
      activeShifts,
      completedShifts,
      noShowShifts: noShows,
      cancelledShifts: cancellations,
      readyWorkers,
      workforceCompliance,
      coverage,
      financialSummary: financials,
    } = sharedMetrics;

    const coverageBase =
      openShifts.length +
      activeShifts.length +
      completedShifts.length;

    const allowedRoles = Array.isArray(
      facility?.allowedRoles
    )
      ? facility.allowedRoles
      : [
          "CNA",
          "LVN",
          "RN",
          "MED_AIDE",
          "CARE_GIVER",
        ];

    const missingRateRoles =
      allowedRoles.filter((role) => {
        const billRate = Number(
          facility?.rates?.[role]?.billRate
        );

        const payRate = Number(
          facility?.rates?.[role]?.payRate
        );

        return !(
          Number.isFinite(billRate) &&
          billRate > 0 &&
          Number.isFinite(payRate) &&
          payRate > 0
        );
      });

    const insights = [];
    const actions = [];

    if (coverage >= 95 && coverageBase > 0) {
      insights.push({
        title: "Strong Coverage",
        description: `${coverage}% of recorded staffing demand is covered.`,
        tone: "positive",
      });
    } else if (
      coverage >= 80 &&
      coverageBase > 0
    ) {
      insights.push({
        title: "Coverage Watch",
        description: `${coverage}% coverage indicates remaining staffing pressure.`,
        tone: "warning",
      });

      actions.push(
        "Prioritize the remaining open shifts."
      );
    } else if (coverageBase > 0) {
      insights.push({
        title: "Coverage Risk",
        description: `${coverage}% coverage requires immediate staffing attention.`,
        tone: "risk",
      });

      actions.push(
        "Escalate open shifts to the planner and qualified worker pool."
      );
    } else {
      insights.push({
        title: "Limited Shift History",
        description:
          "No measurable open or covered demand is currently recorded.",
        tone: "neutral",
      });
    }

    if (openShifts.length > 0) {
      insights.push({
        title: "Open Shift Pressure",
        description: `${openShifts.length} open ${
          openShifts.length === 1
            ? "shift requires"
            : "shifts require"
        } assignment.`,
        tone: "warning",
      });
    }

    if (
      facilityWorkers.length > 0 &&
      workforceCompliance >= 90
    ) {
      insights.push({
        title: "Healthy Workforce Compliance",
        description: `${workforceCompliance}% of connected workers are approved.`,
        tone: "positive",
      });
    } else if (facilityWorkers.length > 0) {
      insights.push({
        title: "Workforce Compliance Gap",
        description: `${workforceCompliance}% of connected workers are approved.`,
        tone: "risk",
      });

      actions.push(
        "Review workers with pending or non-approved compliance."
      );
    } else {
      insights.push({
        title: "No Connected Workforce",
        description:
          "No workers are currently assigned or permitted for this facility.",
        tone: "risk",
      });

      actions.push(
        "Assign qualified workers to the facility."
      );
    }

    if (noShows.length > 0) {
      insights.push({
        title: "No-Show Risk",
        description: `${noShows.length} no-show ${
          noShows.length === 1
            ? "event is"
            : "events are"
        } recorded.`,
        tone: "risk",
      });

      actions.push(
        "Review no-show events and strengthen backup coverage."
      );
    }

    if (cancellations.length > 0) {
      insights.push({
        title: "Cancellation Activity",
        description: `${cancellations.length} cancelled ${
          cancellations.length === 1
            ? "shift is"
            : "shifts are"
        } recorded.`,
        tone: "warning",
      });

      actions.push(
        "Review cancellation causes and recurring patterns."
      );
    }

    if (missingRateRoles.length > 0) {
      insights.push({
        title: "Incomplete Rate Matrix",
        description: `Missing bill or pay rates for: ${missingRateRoles.join(
          ", "
        )}.`,
        tone: "risk",
      });

      actions.push(
        "Complete the facility rate matrix before billing."
      );
    } else {
      insights.push({
        title: "Rates Configured",
        description:
          "All configured roles have bill and pay rates.",
        tone: "positive",
      });
    }

    if (
      showMargin &&
      financials.revenue > 0
    ) {
      if (
        financials.marginPercent >= 20
      ) {
        insights.push({
          title: "Healthy Gross Margin",
          description: `${financials.marginPercent}% gross margin across completed shifts.`,
          tone: "positive",
        });
      } else if (
        financials.marginPercent >= 0
      ) {
        insights.push({
          title: "Low Margin",
          description: `${financials.marginPercent}% gross margin may require rate review.`,
          tone: "warning",
        });

        actions.push(
          "Review facility bill and worker pay rates."
        );
      } else {
        insights.push({
          title: "Negative Margin",
          description: `${financials.marginPercent}% gross margin indicates payroll exceeds revenue.`,
          tone: "risk",
        });

        actions.push(
          "Correct rates before additional shifts are completed."
        );
      }
    }

    if (readyWorkers.length === 0) {
      actions.push(
        "Build a ready workforce pool for this facility."
      );
    }

    if (actions.length === 0) {
      actions.push(
        "Continue normal staffing, compliance, and financial monitoring."
      );
    }

    return {
      coverage,
      hasCoverageData:
        coverageBase > 0,
      openCount: openShifts.length,
      workforceCount:
        facilityWorkers.length,
      readyWorkerCount:
        readyWorkers.length,
      workforceCompliance,
      noShowCount: noShows.length,
      missingRateCount:
        missingRateRoles.length,
      financials,
      insights,
      actions,
    };
  }, [
    facility,
    shifts,
    users,
    showMargin,
  ]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          padding: 17,
          borderRadius: 17,
          color: "#ffffff",
          background:
            "linear-gradient(135deg, #312e81 0%, #7c3aed 100%)",
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 950,
          }}
        >
       Facility Intelligence
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#ede9fe",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
         Rules-based operational intelligence derived from current facility records. 
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
    <InsightCard
  title="Coverage"
  description={
    analysis.hasCoverageData
      ? `${analysis.coverage}%`
      : "No measurable demand"
  }
  tone={
    !analysis.hasCoverageData
      ? "neutral"
      : analysis.coverage >= 95
      ? "positive"
      : analysis.coverage >= 80
      ? "warning"
      : "risk"
  }
/>

        <InsightCard
          title="Open Shifts"
          description={analysis.openCount}
          tone={
            analysis.openCount > 0
              ? "warning"
              : "positive"
          }
        />

        <InsightCard
          title="Ready Workforce"
          description={`${analysis.readyWorkerCount} of ${analysis.workforceCount}`}
          tone={
            analysis.readyWorkerCount > 0
              ? "positive"
              : "risk"
          }
        />

        <InsightCard
          title="Workforce Compliance"
          description={`${analysis.workforceCompliance}%`}
          tone={
            analysis.workforceCompliance >=
            90
              ? "positive"
              : "warning"
          }
        />

        <InsightCard
          title="No-Shows"
          description={analysis.noShowCount}
          tone={
            analysis.noShowCount > 0
              ? "risk"
              : "positive"
          }
        />

        <InsightCard
          title="Missing Rate Roles"
          description={
            analysis.missingRateCount
          }
          tone={
            analysis.missingRateCount > 0
              ? "risk"
              : "positive"
          }
        />
      </div>

      <div style={{ display: "grid", gap: 9 }}>
        <div
          style={{
            color: "#0f172a",
            fontSize: 15,
            fontWeight: 950,
          }}
        >
          Operational Assessment
        </div>

        {analysis.insights.map(
          (insight, index) => (
            <InsightCard
              key={`${insight.title}-${index}`}
              title={insight.title}
              description={
                insight.description
              }
              tone={insight.tone}
            />
          )
        )}
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 15,
          background: "#faf5ff",
          border: "1px solid #ddd6fe",
        }}
      >
        <div
          style={{
            color: "#6d28d9",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Recommended Actions
        </div>

        <div
          style={{
            marginTop: 9,
            display: "grid",
            gap: 7,
            color: "#475569",
            fontSize: 12,
            fontWeight: 750,
            lineHeight: 1.5,
          }}
        >
          {analysis.actions.map(
            (action, index) => (
              <div
                key={`${action}-${index}`}
              >
                • {action}
              </div>
            )
          )}
        </div>
      </div>

      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1.45,
        }}
      >
        These insights are rules-based decision
        support derived from Facility 360 data.
        Future AI Assist can build on this evidence
        layer. Administrators must verify staffing,
        credentials, rates, and facility records
        before acting.
      </div>
    </div>
  );
}